import React, { useEffect, useRef, useState } from "react";
import { Button, Empty, Input, Segmented, Space, Tooltip, Typography } from "antd";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";

const { Text } = Typography;
const { TextArea } = Input;

const MARKDOWN_RENDERERS = {
    ul: ({ children, ...props }) => (
        <ul style={{ paddingLeft: "20px", marginBottom: "16px" }} {...props}>
            {children}
        </ul>
    ),
    ol: ({ children, ...props }) => (
        <ol style={{ paddingLeft: "20px", marginBottom: "16px" }} {...props}>
            {children}
        </ol>
    ),
    li: ({ children, ...props }) => (
        <li style={{ marginBottom: "4px", lineHeight: "1.6" }} {...props}>
            {children}
        </li>
    ),
    p: ({ children, ...props }) => (
        <p style={{ marginBottom: "12px", lineHeight: "1.6" }} {...props}>
            {children}
        </p>
    ),
    h1: ({ children, ...props }) => (
        <h1 style={{ fontSize: "22px", fontWeight: 600, marginBottom: "16px", color: "#1890ff" }} {...props}>
            {children}
        </h1>
    ),
    h2: ({ children, ...props }) => (
        <h2 style={{ fontSize: "18px", fontWeight: 600, marginBottom: "12px", color: "#262626" }} {...props}>
            {children}
        </h2>
    ),
    h3: ({ children, ...props }) => (
        <h3 style={{ fontSize: "15px", fontWeight: 600, marginBottom: "8px", color: "#595959" }} {...props}>
            {children}
        </h3>
    ),
    table: ({ children, ...props }) => (
        <table
            style={{
                width: "100%",
                borderCollapse: "collapse",
                marginBottom: "16px",
                fontSize: "13px",
            }}
            {...props}
        >
            {children}
        </table>
    ),
    th: ({ children, ...props }) => (
        <th
            style={{
                border: "1px solid #e8e8e8",
                padding: "8px 12px",
                backgroundColor: "#fafafa",
                fontWeight: 600,
                textAlign: "left",
            }}
            {...props}
        >
            {children}
        </th>
    ),
    td: ({ children, ...props }) => (
        <td
            style={{
                border: "1px solid #e8e8e8",
                padding: "8px 12px",
            }}
            {...props}
        >
            {children}
        </td>
    ),
    blockquote: ({ children, ...props }) => (
        <blockquote
            style={{
                margin: "0 0 16px",
                padding: "8px 12px",
                borderLeft: "4px solid #d9d9d9",
                background: "#fafafa",
                color: "#595959",
            }}
            {...props}
        >
            {children}
        </blockquote>
    ),
    code: ({ children, ...props }) => (
        <code
            style={{
                padding: "2px 4px",
                borderRadius: 4,
                background: "#f5f5f5",
                fontFamily: "Menlo, Monaco, Consolas, monospace",
            }}
            {...props}
        >
            {children}
        </code>
    ),
};

const MARKDOWN_HELPERS = [
    { key: "h2", label: "H2", tooltip: "插入二级标题", snippet: "## 标题" },
    { key: "bold", label: "粗体", tooltip: "插入粗体文本", snippet: "**加粗文本**" },
    { key: "list", label: "列表", tooltip: "插入无序列表", snippet: "- 列表项" },
    { key: "numbered", label: "编号", tooltip: "插入有序列表", snippet: "1. 列表项" },
    { key: "quote", label: "引用", tooltip: "插入引用块", snippet: "> 引用内容" },
    { key: "code", label: "代码", tooltip: "插入代码片段", snippet: "`代码`" },
];

const MarkdownViewEditor = ({
    value = "",
    onChange,
    placeholder = "请输入 Markdown 内容",
    emptyText = "暂无内容，切换到编辑模式后可录入 Markdown 文本",
    minRows = 14,
    maxRows = 28,
    defaultMode = "preview",
    readOnly = false,
    disabled = false,
}) => {
    const [mode, setMode] = useState(defaultMode);
    const textAreaRef = useRef(null);
    const textValue = value || "";
    const activeMode = readOnly ? "preview" : mode;

    useEffect(() => {
        setMode(defaultMode);
    }, [defaultMode]);

    const insertSnippet = (snippet) => {
        if (!onChange || disabled || readOnly) return;

        const textArea = textAreaRef.current?.resizableTextArea?.textArea;
        if (!textArea) {
            const separator = textValue && !textValue.endsWith("\n") ? "\n" : "";
            onChange(`${textValue}${separator}${snippet}`);
            return;
        }

        const start = textArea.selectionStart ?? textValue.length;
        const end = textArea.selectionEnd ?? textValue.length;
        const before = textValue.slice(0, start);
        const after = textValue.slice(end);
        const nextValue = `${before}${snippet}${after}`;
        onChange(nextValue);

        window.setTimeout(() => {
            textArea.focus();
            const cursor = start + snippet.length;
            textArea.setSelectionRange(cursor, cursor);
        }, 0);
    };

    return (
        <div style={{ width: "100%" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                {!readOnly ? (
                    <Segmented
                        size="small"
                        value={activeMode}
                        onChange={setMode}
                        options={[
                            { label: "查看", value: "preview" },
                            { label: "编辑", value: "write" },
                        ]}
                    />
                ) : (
                    <Text type="secondary">Markdown 预览</Text>
                )}
                {!readOnly && activeMode === "write" && (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        支持 Markdown 语法
                    </Text>
                )}
            </div>

            {activeMode === "write" ? (
                <>
                    <Space size={4} wrap style={{ marginBottom: 8 }}>
                        {MARKDOWN_HELPERS.map((helper) => (
                            <Tooltip key={helper.key} title={helper.tooltip}>
                                <Button size="small" onClick={() => insertSnippet(helper.snippet)} disabled={disabled || readOnly}>
                                    {helper.label}
                                </Button>
                            </Tooltip>
                        ))}
                    </Space>
                    <TextArea
                        ref={textAreaRef}
                        value={textValue}
                        onChange={(event) => onChange?.(event.target.value)}
                        placeholder={placeholder}
                        autoSize={{ minRows, maxRows }}
                        disabled={disabled || readOnly}
                        style={{ fontFamily: "Menlo, Monaco, Consolas, monospace" }}
                    />
                </>
            ) : textValue.trim() ? (
                <div
                    className="markdown-content"
                    style={{
                        minHeight: minRows * 24,
                        padding: "12px 16px",
                        border: "1px solid #f0f0f0",
                        borderRadius: 6,
                        background: "#fff",
                    }}
                >
                    <ReactMarkdown components={MARKDOWN_RENDERERS} remarkPlugins={[remarkGfm, remarkBreaks]}>
                        {textValue}
                    </ReactMarkdown>
                </div>
            ) : (
                <div
                    style={{
                        minHeight: minRows * 24,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        border: "1px dashed #d9d9d9",
                        borderRadius: 6,
                        background: "#fafafa",
                    }}
                >
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={emptyText} />
                </div>
            )}
        </div>
    );
};

export default MarkdownViewEditor;
